import type { ReactNode } from "react";
import { useSyncExternalStore } from "react";
import type {
  ToastEntry,
  ToastOptions,
  ToastPosition,
} from "@UI/types/notifications.ts";

/**
 * Toast store — a module-level singleton.
 *
 * Writers (`addToast`/`updateToast`/`dismissToast`) are callable from anywhere,
 * including outside the React tree (axios/query caches, `window` listeners,
 * plain utils). Components read through the `useToasts` hook (the `subscribe`
 * side stays private). `getToastsSnapshot` is also exported so the pure
 * (node, non-React) unit test can read state without rendering.
 */

const DEFAULT_DURATION = 5000;
const DEFAULT_POSITION: ToastPosition = "bottom-center";

let _toasts: ToastEntry[] = [];
let _seq = 0;
const _listeners = new Set<() => void>();

const notify = (): void => _listeners.forEach(listener => listener());

/** Stable snapshot — reference changes only on mutation. Read via `useToasts` in components. */
export const getToastsSnapshot = (): ToastEntry[] => _toasts;

const subscribe = (listener: () => void): (() => void) => {
  _listeners.add(listener);
  return () => {
    _listeners.delete(listener);
  };
};

const clearTimer = (entry: ToastEntry): void => {
  if (entry.timeout) clearTimeout(entry.timeout);
};

const scheduleTimer = (entry: ToastEntry): void => {
  if (entry.duration === false) return;
  entry.timeout = setTimeout(() => dismissToast(entry.id), entry.duration);
};

/** Add (or upsert by id) a toast. Returns its id. */
export const addToast = (
  content: ReactNode,
  options: ToastOptions = {},
): string => {
  const id = options.id ?? `toast-${++_seq}`;

  const existing = _toasts.find(toast => toast.id === id);
  if (existing) clearTimer(existing);

  const entry: ToastEntry = {
    id,
    content,
    state: options.state ?? "neutral",
    duration: options.duration ?? DEFAULT_DURATION,
    position: options.position ?? DEFAULT_POSITION,
    dismissible: options.dismissible ?? true,
    plain: options.plain ?? false,
    description: options.description,
    action: options.action,
    onClose: options.onClose,
  };
  scheduleTimer(entry);

  _toasts = [..._toasts.filter(toast => toast.id !== id), entry];
  notify();
  return id;
};

/** Patch an existing toast in place (e.g. live progress). Resets its timer. */
export const updateToast = (
  id: string,
  patch: Partial<ToastOptions> & { content?: ReactNode },
): void => {
  const existing = _toasts.find(toast => toast.id === id);
  if (!existing) return;
  clearTimer(existing);

  const updated: ToastEntry = {
    ...existing,
    ...patch,
    content: patch.content ?? existing.content,
    timeout: undefined,
  };
  scheduleTimer(updated);

  _toasts = _toasts.map(toast => (toast.id === id ? updated : toast));
  notify();
};

/** Dismiss one toast by id, or all when called without an id. */
export const dismissToast = (id?: string): void => {
  if (id === undefined) {
    if (_toasts.length === 0) return;
    _toasts.forEach(toast => {
      clearTimer(toast);
      toast.onClose?.();
    });
    _toasts = [];
    notify();
    return;
  }

  const existing = _toasts.find(toast => toast.id === id);
  if (!existing) return;
  clearTimer(existing);
  existing.onClose?.();

  _toasts = _toasts.filter(toast => toast.id !== id);
  notify();
};

/** Subscribe a component to the live toast list. */
export const useToasts = (): ToastEntry[] =>
  useSyncExternalStore(subscribe, getToastsSnapshot);
